import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, UserCheck, UserCog, Trash2, Shield, Plus, MapPin, Edit, RefreshCw, Wrench, CheckCircle2, AlertCircle, AlertTriangle, Loader2, Calendar, Clock, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentUserLocationId, setCurrentUserLocationId] = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [cashCacheClearing, setCashCacheClearing] = useState(false);
  const [cashCacheStatus, setCashCacheStatus] = useState(null); // {deleted_count, from_date, to_date, backfill_pct}
  const [cashBackfillRunning, setCashBackfillRunning] = useState(false);
  // Single-location targeted recompute (deep-history quota bypass, e.g. Chalfont)
  const [singleLocId, setSingleLocId] = useState("");
  const [singleYear, setSingleYear] = useState(String(new Date().getFullYear()));
  const [singleRunning, setSingleRunning] = useState(false);
  const [singleStatus, setSingleStatus] = useState(null);
  const [forceRecompute, setForceRecompute] = useState(false);   // all-locations: bypass skip-logic
  const [singleForce, setSingleForce] = useState(false);         // single-location: bypass skip-logic
  const [rebuildRunning, setRebuildRunning] = useState(false);
  const [rebuildStatus, setRebuildStatus] = useState(null);
  const [rebuildAllRunning, setRebuildAllRunning] = useState(false);
  const [rebuildAllStatus, setRebuildAllStatus] = useState(null);
  const [rebuildSchedule, setRebuildSchedule] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const navigate = useNavigate();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [locationChangeRequests, setLocationChangeRequests] = useState([]);
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'agent',
    location_id: '',
    send_welcome_email: true
  });

  useEffect(() => {
    fetchUsers();
    fetchLocations();
    fetchLocationChangeRequests();
  }, []);

  // Show banner if a backfill is already running when the page mounts
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/backfill-2025-status`);
        if (r.data?.status === "running") {
          setBackfillRunning(true);
          setBackfillStatus(r.data);
          // Resume polling
          const tick = async () => {
            const done = await pollBackfillStatus();
            if (!done) setTimeout(tick, 5000);
          };
          setTimeout(tick, 5000);
        } else if (r.data && r.data.status !== "never_run") {
          setBackfillStatus(r.data);
        }
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users);
      setIsMasterAdmin(response.data.is_master_admin || false);
      
      // Get current user's location from their own user data
      const cachedUserRaw = localStorage.getItem('cachedUser');
      let currentEmail = null;
      try {
        if (cachedUserRaw) currentEmail = JSON.parse(cachedUserRaw)?.email || null;
      } catch (_) { /* noop */ }
      const currentUser = response.data.users.find(u => u.email === currentEmail);
      if (currentUser && currentUser.location_id) {
        setCurrentUserLocationId(currentUser.location_id);
        // Pre-fill location for location admins
        setNewUser(prev => ({ ...prev, location_id: currentUser.location_id }));
      }
      setIsSuperAdmin(currentUser?.role === 'super_admin');
    } catch (error) {
      // 401/403 handled by global axios interceptor in App.js
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        toast.error("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Failed to load locations", error);
    }
  };

  // ── 2025 Baselines Backfill (Super Admin) ──────────────────────────
  // Recomputes 2025 monthly occupancy actuals per location from Gingr and
  // wipes the occupancy cache. Fixes the "all locations show identical 2025 %"
  // YoY data bug. Safe to re-run; runs in the background; ~60-90s per location.
  const pollBackfillStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/backfill-2025-status`);
      setBackfillStatus(r.data);
      const status = r.data?.status;
      if (status === "completed") {
        setBackfillRunning(false);
        const cleared = r.data?.occupancy_cache_rows_cleared ?? 0;
        const totalLocs = r.data?.total_locations ?? 0;
        toast.success(`2025 baselines refreshed for ${totalLocs} location(s). Cache cleared (${cleared} rows). Hard-refresh the Occupancy tab to see updated values.`);
        return true;
      }
      if (status === "failed") {
        setBackfillRunning(false);
        toast.error(`Backfill failed: ${r.data?.error || "unknown error"}`);
        return true;
      }
      return false;
    } catch (e) {
      console.error("poll backfill status failed", e);
      return false;
    }
  };

  const runBackfill2025 = async () => {
    if (backfillRunning) return;
    if (!window.confirm(
      "Re-sync 2025 baselines for ALL locations?\n\nThis recomputes the YoY 'PY 2025 %' values shown in Occupancy → Monthly Breakdown. Each location takes ~60-90 seconds. Safe to run; existing data is upserted in place."
    )) return;

    setBackfillRunning(true);
    setBackfillStatus(null);
    try {
      const r = await axios.post(`${API}/admin/backfill-2025-actuals`);
      if (r.data?.ok === false) {
        toast.warning(r.data.message || "Backfill already in progress.");
      } else {
        toast.info(`Backfill started for ${r.data?.total_locations || 0} location(s). Polling for completion…`);
      }
      // Poll every 5 sec until done (max ~10 min)
      const maxPolls = 120;
      let polls = 0;
      const tick = async () => {
        polls += 1;
        const done = await pollBackfillStatus();
        if (done || polls >= maxPolls) return;
        setTimeout(tick, 5000);
      };
      setTimeout(tick, 4000);
    } catch (err) {
      setBackfillRunning(false);
      const msg = err.response?.data?.detail || "Failed to start backfill";
      toast.error(msg);
    }
  };

  // ── Cash Revenue: in-place recompute (all locations, daytime-safe) ──
  const pollRecompute = async () => {
    try {
      const r = await axios.get(`${API}/cash-revenue/recompute-all/status`);
      const s = r.data;
      setCashCacheStatus(s);
      if (s.status === "running") {
        setCashBackfillRunning(true);
        setCashCacheClearing(true);
        setTimeout(pollRecompute, 6000);
      } else {
        setCashBackfillRunning(false);
        setCashCacheClearing(false);
        if (s.status === "complete") {
          toast.success(`Cash recompute complete — ${s.completed_locations}/${s.total_locations} locations.`);
        } else if (s.status === "failed") {
          toast.error(`Recompute failed: ${s.error || "unknown"}`);
        }
      }
    } catch { setTimeout(pollRecompute, 8000); }
  };

  const startRecompute = async (payload, confirmMsg) => {
    if (cashCacheClearing || cashBackfillRunning) return;
    if (!window.confirm(confirmMsg)) return;
    setCashCacheClearing(true);
    setCashBackfillRunning(true);
    try {
      await axios.post(`${API}/cash-revenue/recompute-all`, payload);
      toast.success("Recompute started. Live progress shown below.");
      setTimeout(pollRecompute, 3000);
    } catch (err) {
      setCashBackfillRunning(false);
      setCashCacheClearing(false);
      toast.error(err.response?.data?.detail || "Failed to start recompute");
    }
  };

  const recomputeAllCashInPlace = () => startRecompute(
    { force: forceRecompute },
    (forceRecompute
      ? "FORCE recompute current-year cash revenue for ALL locations?\n\n" +
        "Re-fetches EVERY day fresh from Gingr — even days already stored — so any day previously " +
        "captured short (e.g. during API throttling) is corrected. Heavier on the Gingr API; best run off-hours. "
      : "Recompute current-year cash revenue for ALL locations (in-place)?\n\n" +
        "Rebuilds every day from Jan 1 to yesterday using the latest Gingr logic, overwriting " +
        "each day as it goes. ") +
    "Safe to run during business hours — the dashboard never drops to $0. " +
    "Takes ~5-15 min per location and runs in the background."
  );

  const backfillPriorYearCash = () => startRecompute(
    { year: 2025 },
    "Backfill 2025 cash revenue for ALL locations?\n\n" +
    "This is a one-time job that enables MTD and YTD year-over-year (YoY) comparisons. " +
    "It rebuilds the entire 2025 calendar year for every location, so it's large and can take " +
    "1-2 hours total, running in the background. Safe to run during the day (in-place, never blanks)."
  );

  // ── Single-location targeted recompute (deep-history quota bypass) ──
  const pollSingleRecompute = async (locId) => {
    try {
      const r = await axios.get(`${API}/cash-revenue/recompute-location/status`, { params: { location_id: locId } });
      const s = r.data;
      setSingleStatus(s);
      if (s.status === "running" || (s.status || "").startsWith("retrying")) {
        setSingleRunning(true);
        setTimeout(() => pollSingleRecompute(locId), 6000);
      } else {
        setSingleRunning(false);
        if (s.status === "complete") {
          toast.success(`Recompute complete — ${s.days_processed || 0} days processed (${s.days_skipped || 0} skipped).`);
        } else if (s.status === "failed") {
          toast.error(`Recompute failed: ${s.error || "unknown"}`);
        }
      }
    } catch { setTimeout(() => pollSingleRecompute(locId), 8000); }
  };

  const startSingleRecompute = async () => {
    if (singleRunning) return;
    if (!singleLocId) { toast.error("Pick a location first"); return; }
    const locName = getLocationName(singleLocId);
    if (!window.confirm(
      `Recompute ${singleYear} cash revenue for ${locName}${singleForce ? " (FORCE full re-fetch)" : ""} only?\n\n` +
      (singleForce
        ? `Re-fetches EVERY day of ${singleYear} fresh from Gingr — even days already stored — so any day ` +
          `captured short under throttling is corrected. Heavier on the API; best run off-hours. `
        : `Scopes the rebuild to a single calendar year so deep-history locations stay under ` +
          `Gingr's API quota. `) +
      `Runs in place (never blanks to $0) and self-heals any $0 days.`
    )) return;
    setSingleRunning(true);
    setSingleStatus({ status: "running", from_date: `${singleYear}-01-01` });
    try {
      await axios.post(`${API}/cash-revenue/recompute-location`, {
        location_id: singleLocId,
        year: Number(singleYear),
        force: singleForce,
      });
      toast.success(`Recompute started for ${locName} (${singleYear}).`);
      setTimeout(() => pollSingleRecompute(singleLocId), 3000);
    } catch (err) {
      setSingleRunning(false);
      toast.error(err.response?.data?.detail || "Failed to start recompute");
    }
  };

  const pollRebuild = async (locId) => {
    try {
      const r = await axios.get(`${API}/cash-revenue/rebuild/status?location_id=${locId}`);
      setRebuildStatus(r.data);
      const active = r.data.status === "running" || String(r.data.status || "").startsWith("retrying");
      if (active) {
        setTimeout(() => pollRebuild(locId), 5000);
      } else {
        setRebuildRunning(false);
        if (r.data.status === "complete") toast.success("Full rebuild complete — cash now matches Gingr Net Payments.");
        else if (r.data.status === "completed_with_gaps") toast.warning("Rebuild finished with some gaps (throttled windows). Re-run to fill them.");
        else if (r.data.status === "cancelled") toast.warning("Rebuild stopped.");
        else if (r.data.status === "failed") toast.error("Rebuild failed: " + (r.data.error || "unknown"));
      }
    } catch (_) { setRebuildRunning(false); }
  };

  const startFullRebuild = async () => {
    if (rebuildRunning) return;
    if (!singleLocId) { toast.error("Pick a location first"); return; }
    const locName = getLocationName(singleLocId);
    if (!window.confirm(
      `Full cash REBUILD for ${locName} (2024 → today)?\n\n` +
      `Scans EVERY invoice since 2024 and re-buckets each payment by the day it was collected — ` +
      `capturing advance deposits, prepaid packages and recurring-membership payments that the ` +
      `normal recompute misses. This makes Cash Collected match Gingr's Net Payments report.\n\n` +
      `Heavy one-time job (runs in the background, may take a while). Best run off-hours.`
    )) return;
    setRebuildRunning(true);
    setRebuildStatus({ status: "running" });
    try {
      await axios.post(`${API}/cash-revenue/rebuild`, { location_id: singleLocId, start_date: "2024-01-01" });
      toast.success(`Full rebuild started for ${locName}.`);
      setTimeout(() => pollRebuild(singleLocId), 4000);
    } catch (err) {
      setRebuildRunning(false);
      toast.error(err.response?.data?.detail || "Failed to start rebuild");
    }
  };

  const stopFullRebuild = async () => {
    if (!singleLocId) return;
    if (!window.confirm("Stop the current rebuild for this location? It halts at the next window (or clears immediately if it's already stalled).")) return;
    try {
      const r = await axios.post(`${API}/cash-revenue/rebuild/cancel?location_id=${singleLocId}`);
      toast.success(r.data?.message || "Stop requested.");
      setTimeout(() => pollRebuild(singleLocId), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to stop rebuild");
    }
  };

  const fmtAgo = (s) => (s == null ? "" : s < 90 ? `${Math.round(s)}s` : s < 5400 ? `${Math.round(s / 60)}m` : `${Math.round(s / 3600)}h`);

  // Rebuild start/finish timestamps (local timezone), e.g. "Started Sep 19, 3:42 PM → Finished 4:15 PM (33 min)".
  const fmtClock = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return null; }
  };
  const fmtDuration = (startIso, endIso) => {
    if (!startIso || !endIso) return "";
    try {
      const ms = new Date(endIso) - new Date(startIso);
      if (ms <= 0) return "";
      const mins = Math.round(ms / 60000);
      if (mins < 1) return "<1 min";
      if (mins < 60) return `${mins} min`;
      const h = Math.floor(mins / 60), m = mins % 60;
      return m ? `${h}h ${m}m` : `${h}h`;
    } catch { return ""; }
  };
  const fmtRebuildWindow = (startIso, endIso) => {
    const s = fmtClock(startIso);
    if (!s) return null;
    const e = fmtClock(endIso);
    if (e) {
      const dur = fmtDuration(startIso, endIso);
      return `Started ${s} → Finished ${e}${dur ? ` (${dur})` : ""}`;
    }
    return `Started ${s}`;
  };
  // Friendly label for rebuild statuses (raw backend strings look scary to users).
  const rebuildStatusLabel = (st) => {
    const m = {
      complete: "complete",
      complete_minor_gaps: "complete (minor gap)",
      completed_with_gaps: "needs retry (gaps)",
      completed_with_errors: "finished with errors",
      failed: "failed",
      running: "running",
      pending: "pending",
      cancelled: "cancelled",
    };
    return m[st] || st;
  };

  const pollRebuildAll = async () => {
    try {
      const r = await axios.get(`${API}/cash-revenue/rebuild-all/status`);
      setRebuildAllStatus(r.data);
      const active = r.data.status === "running" || String(r.data.status || "").startsWith("retrying");
      if (active) {
        setTimeout(pollRebuildAll, 6000);
      } else {
        setRebuildAllRunning(false);
        if (r.data.status === "complete") toast.success("All locations rebuilt — cash now matches Gingr Net Payments system-wide.");
        else if (r.data.status === "completed_with_errors") toast.warning("Rebuild-all finished with some locations needing a retry.");
        else if (r.data.status === "interrupted") toast.warning("Rebuild-all was interrupted and hit the auto-resume cap. Click Rebuild All Locations to restart.");
      }
    } catch (_) { setRebuildAllRunning(false); }
  };

  const startRebuildAll = async () => {
    if (rebuildAllRunning) return;
    if (!window.confirm(
      `Full cash REBUILD for ALL locations (2024 → today)?\n\n` +
      `Runs the payment-date-accurate rebuild for every location one at a time, so cash across ` +
      `the entire system matches Gingr's Net Payments. Heavy the FIRST time (it fills the settled-` +
      `transaction cache); subsequent rebuilds are much faster. Runs in the background — best off-hours.`
    )) return;
    setRebuildAllRunning(true);
    setRebuildAllStatus({ status: "running" });
    try {
      await axios.post(`${API}/cash-revenue/rebuild-all`, { start_date: "2024-01-01" });
      toast.success("System-wide rebuild started.");
      setTimeout(pollRebuildAll, 4000);
    } catch (err) {
      setRebuildAllRunning(false);
      toast.error(err.response?.data?.detail || "Failed to start rebuild-all");
    }
  };

  const stopRebuildAll = async () => {
    if (!window.confirm("Stop the current all-locations rebuild? Locations already finished keep their data; the current one halts at its next window.")) return;
    try {
      const r = await axios.post(`${API}/cash-revenue/rebuild-all/cancel`);
      toast.success(r.data?.message || "Stop requested.");
      setTimeout(pollRebuildAll, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to stop rebuild");
    }
  };

  // Resume cash recompute progress on mount (so progress is visible across reloads)
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/cash-revenue/recompute-all/status`);
        if (r.data && r.data.status && r.data.status !== "not_started") {
          setCashCacheStatus(r.data);
          if (r.data.status === "running") {
            setCashBackfillRunning(true);
            setCashCacheClearing(true);
            setTimeout(pollRecompute, 3000);
          }
        }
      } catch (_) { /* noop */ }
      // Also resume the full REBUILD-all tracker so its progress survives a page refresh.
      try {
        const rb = await axios.get(`${API}/cash-revenue/rebuild-all/status`);
        if (rb.data && rb.data.status && rb.data.status !== "not_started") {
          setRebuildAllStatus(rb.data);
          const active = rb.data.status === "running" || String(rb.data.status).startsWith("retrying");
          if (active) {
            setRebuildAllRunning(true);
            setTimeout(pollRebuildAll, 3000);
          }
        }
      } catch (_) { /* noop */ }
      // Load the automatic full-rebuild schedule (weekly fail-safe + one-time armed).
      try {
        const sc = await axios.get(`${API}/cash-revenue/rebuild-schedule`);
        setRebuildSchedule(sc.data);
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // Resume single-location rebuild progress when a location is selected (survives reloads).
  useEffect(() => {
    if (!isSuperAdmin || !singleLocId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/cash-revenue/rebuild/status?location_id=${singleLocId}`);
        if (r.data && r.data.status && r.data.status !== "not_started") {
          setRebuildStatus(r.data);
          const active = r.data.status === "running" || String(r.data.status).startsWith("retrying");
          if (active && !rebuildRunning) {
            setRebuildRunning(true);
            setTimeout(() => pollRebuild(singleLocId), 1500);
          }
        }
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleLocId, isSuperAdmin]);

  const loadRebuildSchedule = async () => {
    try {
      const sc = await axios.get(`${API}/cash-revenue/rebuild-schedule`);
      setRebuildSchedule(sc.data);
    } catch (_) { /* noop */ }
  };

  const armRunTonight = async () => {
    setScheduleSaving(true);
    try {
      const r = await axios.post(`${API}/cash-revenue/rebuild-schedule/run-tonight`);
      toast.success(r.data?.message || "Full rebuild armed for tonight (off-hours).");
      await loadRebuildSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to arm tonight's rebuild");
    } finally { setScheduleSaving(false); }
  };

  const cancelRunTonight = async () => {
    setScheduleSaving(true);
    try {
      await axios.post(`${API}/cash-revenue/rebuild-schedule/cancel-tonight`);
      toast.success("Pending one-time rebuild cancelled.");
      await loadRebuildSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    } finally { setScheduleSaving(false); }
  };

  const toggleWeekly = async (enabled) => {
    setScheduleSaving(true);
    try {
      const r = await axios.put(`${API}/cash-revenue/rebuild-schedule`, {
        weekly_enabled: enabled,
        weekly_weekday: rebuildSchedule?.weekly_weekday ?? 6,
        start_date: "2024-01-01",
      });
      setRebuildSchedule(r.data);
      toast.success(enabled ? "Weekly auto-rebuild enabled (Sundays ~1 AM ET)." : "Weekly auto-rebuild disabled.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update schedule");
    } finally { setScheduleSaving(false); }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) {
      toast.error("All fields are required");
      return;
    }

    if (!newUser.location_id) {
      toast.error("Please select a location");
      return;
    }

    if (newUser.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    try {
      // Unified endpoint: location admins can create users in their own location;
      // super admins can create users in any location.
      const res = await axios.post(`${API}/admin/users/create`, {
        email: newUser.email,
        password: newUser.password,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        role: newUser.role,
        location_id: newUser.location_id,
        send_welcome_email: !!newUser.send_welcome_email,
      });
      const welcomeSent = res?.data?.welcome_email_sent;
      toast.success(
        welcomeSent
          ? "User created and welcome email sent!"
          : (newUser.send_welcome_email
              ? "User created (welcome email failed — see logs)"
              : "User created successfully!")
      );
      setCreateDialogOpen(false);
      setNewUser({ 
        first_name: '', 
        last_name: '', 
        email: '', 
        password: '', 
        role: 'agent', 
        location_id: currentUserLocationId || '',
        send_welcome_email: true
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/role?role=${newRole}`);
      toast.success("User role updated successfully!");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user role");
    }
  };

  const updateUserLocation = async (userId, locationId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/location?location_id=${locationId}`);
      toast.success("User location updated successfully!");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user location");
    }
  };

  const fetchLocationChangeRequests = async () => {
    try {
      const response = await axios.get(`${API}/location-change-requests`);
      setLocationChangeRequests(response.data.requests || []);
    } catch (error) {
      console.error("Failed to load location change requests", error);
    }
  };

  const approveLocationChangeRequest = async (requestId) => {
    try {
      await axios.post(`${API}/location-change-requests/${requestId}/approve`);
      toast.success("Location change request approved!");
      fetchLocationChangeRequests();
      fetchUsers();
    } catch (error) {
      toast.error("Failed to approve request");
    }
  };

  const rejectLocationChangeRequest = async (requestId) => {
    try {
      await axios.post(`${API}/location-change-requests/${requestId}/reject`);
      toast.success("Location change request rejected");
      fetchLocationChangeRequests();
    } catch (error) {
      toast.error("Failed to reject request");
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted successfully!");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openEditDialog = (user) => {
    setEditingUser({
      id: user.id,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email,
      role: user.role,
      new_password: '',
      confirm_password: ''
    });
    setEditDialogOpen(true);
  };

  const updateUserName = async () => {
    if (!editingUser.first_name || !editingUser.last_name) {
      toast.error("Both first and last name are required");
      return;
    }

    try {
      await axios.patch(`${API}/admin/users/${editingUser.id}/name`, null, {
        params: {
          first_name: editingUser.first_name,
          last_name: editingUser.last_name
        }
      });
      toast.success("User name updated successfully!");
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user name");
    }
  };

  const resetUserPassword = async () => {
    if (!editingUser.new_password) {
      toast.error("Please enter a new password");
      return;
    }

    if (editingUser.new_password !== editingUser.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    if (editingUser.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    try {
      await axios.post(`${API}/admin/users/${editingUser.id}/reset-password`, {
        new_password: editingUser.new_password
      });
      toast.success("Password reset successfully!");
      setEditingUser({...editingUser, new_password: '', confirm_password: ''});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'admin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'manager':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'agent':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'tenant_admin':
        return 'Tenant admin';
      case 'manager':
        return 'Manager';
      case 'agent':
        return 'Staff';
      default:
        return role;
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : 'Not Assigned';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-600">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-1 sm:mb-2" style={{ fontFamily: 'Manrope' }}>
            User Management
          </h1>
          <p className="text-sm sm:text-lg text-gray-600">{users.length} total users</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Shield className="h-4 w-4" />
            Admin Only
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      type="text"
                      placeholder="John"
                      value={newUser.first_name || ''}
                      onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      type="text"
                      placeholder="Doe"
                      value={newUser.last_name || ''}
                      onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin (Full Location Access)</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="agent">Staff (Location Access)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {newUser.role === 'admin' && 'Location Admin: Manage users, view revenue, full access to this location'}
                    {newUser.role === 'manager' && 'Manager: Run daily ops with revenue access for this location'}
                    {newUser.role === 'agent' && 'Staff: Lead management for this location, no revenue access'}
                  </p>
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Select 
                    value={newUser.location_id} 
                    onValueChange={(value) => setNewUser({...newUser, location_id: value})}
                    disabled={!isMasterAdmin}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(location => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name} - {location.city}, {location.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {isMasterAdmin
                      ? 'Assign user to a specific location'
                      : 'New users are created under your location'}
                  </p>
                </div>
                <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <input
                    type="checkbox"
                    id="send_welcome_email"
                    checked={!!newUser.send_welcome_email}
                    onChange={(e) => setNewUser({ ...newUser, send_welcome_email: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-emerald-400 text-emerald-600 focus:ring-emerald-500"
                    data-testid="send-welcome-email-checkbox"
                  />
                  <Label htmlFor="send_welcome_email" className="text-xs leading-snug cursor-pointer">
                    <span className="font-semibold text-emerald-900">Send invite email</span>
                    <span className="block text-emerald-800 mt-0.5">
                      Emails a one-time Set password link (expires in 7 days). The password you set here also works.
                    </span>
                  </Label>
                </div>

                {/* Role permissions reference (visible inside dialog so location admins know what each role unlocks) */}
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs space-y-1.5" data-testid="role-permissions-help">
                  <div className="font-semibold text-gray-700 mb-1">Role permissions</div>
                  <div><span className="font-semibold text-purple-700">Admin:</span> <span className="text-gray-700">Manage users, revenue access, settings, full location access</span></div>
                  <div><span className="font-semibold text-blue-700">Manager:</span> <span className="text-gray-700">Daily ops + revenue data + staff oversight</span></div>
                  <div><span className="font-semibold text-gray-700">Staff:</span> <span className="text-gray-700">Lead management only, no revenue access</span></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={createUser} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                  Create User
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit User Name Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
                <DialogDescription>
                  Update details for {editingUser?.email}
                </DialogDescription>
              </DialogHeader>
              {editingUser && (
                <div className="space-y-4 py-4">
                  {/* Name Section */}
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="edit_first_name">First Name</Label>
                      <Input
                        id="edit_first_name"
                        type="text"
                        placeholder="John"
                        value={editingUser.first_name}
                        onChange={(e) => setEditingUser({...editingUser, first_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_last_name">Last Name</Label>
                      <Input
                        id="edit_last_name"
                        type="text"
                        placeholder="Doe"
                        value={editingUser.last_name}
                        onChange={(e) => setEditingUser({...editingUser, last_name: e.target.value})}
                      />
                    </div>
                    <Button onClick={updateUserName} className="w-full" variant="outline">
                      Update Name
                    </Button>
                  </div>

                  {/* Password Reset Section (Super Admin Only) */}
                  {isMasterAdmin && (
                    <div className="space-y-3 pt-4 border-t">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        🔒 Reset Password (Super Admin Only)
                      </div>
                      <div>
                        <Label htmlFor="new_password">New Password</Label>
                        <Input
                          id="new_password"
                          type="password"
                          placeholder="Min 8 characters"
                          value={editingUser.new_password || ''}
                          onChange={(e) => setEditingUser({...editingUser, new_password: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirm_password">Confirm Password</Label>
                        <Input
                          id="confirm_password"
                          type="password"
                          placeholder="Confirm new password"
                          value={editingUser.confirm_password || ''}
                          onChange={(e) => setEditingUser({...editingUser, confirm_password: e.target.value})}
                        />
                      </div>
                      <Button 
                        onClick={resetUserPassword} 
                        className="w-full bg-red-600 hover:bg-red-700"
                        disabled={!editingUser.new_password || !editingUser.confirm_password}
                      >
                        Reset Password
                      </Button>
                      <p className="text-xs text-gray-500">
                        Use this to help locked out admins or super admins
                      </p>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
            <Users className="h-5 w-5 text-cyan-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              {users.length}
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Admins</CardTitle>
            <UserCog className="h-5 w-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              {users.filter(u => ['admin', 'tenant_admin'].includes(u.role)).length}
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Users</CardTitle>
            <UserCheck className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              {users.filter(u => u.last_login).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="card">
        <CardHeader>
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">User</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Location</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Created</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Last Login</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                          {(user.first_name || user.email).charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}` 
                              : user.email}
                          </span>
                          {user.first_name && user.last_name && (
                            <span className="text-xs text-gray-500">{user.email}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {user.role === 'tenant_admin' ? (
                        <span
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border bg-amber-100 text-amber-800 border-amber-200"
                          title="Sees every location in this tenant. Change in Tenant Management."
                          data-testid={`tenant-admin-tag-${user.id}`}
                        >
                          Tenant admin
                        </span>
                      ) : (
                      <Select
                        value={user.role}
                        onValueChange={(newRole) => updateUserRole(user.id, newRole)}
                      >
                        <SelectTrigger className={`w-32 border ${getRoleBadgeColor(user.role)}`}>
                          <SelectValue>
                            {getRoleDisplayName(user.role)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {isMasterAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="agent">Staff</SelectItem>
                        </SelectContent>
                      </Select>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      {isMasterAdmin ? (
                        <Select
                          value={user.location_id || "none"}
                          onValueChange={(newLocationId) => {
                            if (newLocationId !== "none") {
                              updateUserLocation(user.id, newLocationId);
                            }
                          }}
                        >
                          <SelectTrigger className="w-48 border border-gray-200">
                            <SelectValue>
                              <div className="flex items-center gap-1 text-sm">
                                <MapPin className="h-3 w-3" />
                                {getLocationName(user.location_id)}
                              </div>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map((location) => (
                              <SelectItem key={location.id} value={location.id}>
                                {location.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="h-3 w-3" />
                          {getLocationName(user.location_id)}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                    </td>
                    <td className="py-4 px-4">
                      {user.role === 'tenant_admin' && !isMasterAdmin ? (
                        <span className="text-xs text-gray-400" data-testid={`tenant-admin-managed-${user.id}`}>Managed by super admin</span>
                      ) : (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteUser(user.id, user.email)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Location Change Requests (Master Admin Only) */}
      {isMasterAdmin && locationChangeRequests.length > 0 && (
        <Card className="card bg-yellow-50 border-yellow-200">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <MapPin className="h-5 w-5 text-yellow-600" />
              Pending Location Change Requests ({locationChangeRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {locationChangeRequests.map((request) => (
                <div key={request.id} className="p-4 bg-white rounded-lg border border-yellow-200 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{request.user_email}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Requesting to change location to: <span className="font-semibold text-blue-600">{request.requested_location_name}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Requested: {new Date(request.requested_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => approveLocationChangeRequest(request.id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rejectLocationChangeRequest(request.id)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Role Permissions</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div>
              <span className="font-semibold text-purple-700">Master Admin:</span>
              <span className="text-gray-700"> All locations, create locations, reassign users, full system access</span>
            </div>
            <div>
              <span className="font-semibold text-purple-700">Location Admin:</span>
              <span className="text-gray-700"> Assigned location only, manage users, revenue access, settings access</span>
            </div>
            <div>
              <span className="font-semibold text-blue-700">Manager:</span>
              <span className="text-gray-700"> Assigned location access, revenue data, staff oversight</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">Staff:</span>
              <span className="text-gray-700"> Assigned location only, no revenue access, lead management</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Location-Based Access</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2 text-gray-700">
            <p>✅ Staff users only see leads from their assigned location</p>
            <p>✅ Dashboard and reports filtered by location</p>
            <p>✅ Multi-location rollout ready</p>
            <p>📍 Current Locations: {locations.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* ── System Maintenance (Super Admin only) ──────────────────── */}
      {isSuperAdmin && (
        <Card className="card border-amber-200 bg-amber-50/40" data-testid="admin-maintenance-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Wrench className="h-4 w-4 text-amber-700" />
              System Maintenance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-3 bg-white rounded-lg border border-amber-200">
              <div className="flex-1">
                <div className="font-semibold text-gray-900 text-sm">Re-sync 2025 Occupancy Baselines</div>
                <p className="text-xs text-gray-600 mt-1 max-w-2xl">
                  Recomputes the 2025 monthly per-location actuals shown as the YoY "PY %" column on
                  Daily Ops → Occupancy → Monthly Breakdown. Run this once on production after deployment
                  if every location is showing identical 2025 percentages. Hits all locations at once;
                  ~60-90 seconds per location; runs in the background; safe to re-run.
                </p>
                {backfillStatus && backfillStatus.status && backfillStatus.status !== "never_run" && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                    {backfillStatus.status === "running" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold">
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        Running · {backfillStatus.completed_locations || 0}/{backfillStatus.total_locations || 0} locations
                        {backfillStatus.progress?.current_location && ` · ${backfillStatus.progress.current_location} (month ${backfillStatus.progress.current_month || "—"}/12)`}
                      </span>
                    )}
                    {backfillStatus.status === "completed" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                        <CheckCircle2 className="h-2.5 w-2.5" />
                        Last completed {backfillStatus.finished_at ? new Date(backfillStatus.finished_at).toLocaleString() : ""}
                        {typeof backfillStatus.occupancy_cache_rows_cleared === "number" && ` · ${backfillStatus.occupancy_cache_rows_cleared} cache rows cleared`}
                      </span>
                    )}
                    {backfillStatus.status === "failed" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">
                        <AlertCircle className="h-2.5 w-2.5" />
                        Failed: {backfillStatus.error || "unknown"}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Button
                onClick={runBackfill2025}
                disabled={backfillRunning}
                className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap shrink-0"
                data-testid="btn-resync-2025-baselines"
              >
                {backfillRunning ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Running…</>
                ) : (
                  <><RefreshCw className="h-3.5 w-3.5 mr-2" /> Re-sync 2025 Baselines</>
                )}
              </Button>
            </div>

            {/* ── Recompute Cash Revenue (all locations, in-place) ────── */}
            <div className="p-3 bg-white rounded-lg border border-emerald-200">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-sm">Recompute Cash Revenue (all locations)</div>
                  <p className="text-xs text-gray-600 mt-1 max-w-2xl">
                    Rebuilds every daily cash total for <strong>all locations</strong> using the latest Gingr
                    logic. Runs <strong>in place</strong> &mdash; each day is overwritten as it&rsquo;s recomputed,
                    so the dashboard never drops to $0. <strong>Safe to run during the day.</strong> Run once on
                    production after deploying a cash-logic fix. The <strong>2025</strong> button is a one-time
                    backfill that unlocks MTD/YTD year-over-year comparisons.
                  </p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    onClick={recomputeAllCashInPlace}
                    disabled={cashCacheClearing || cashBackfillRunning}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                    data-testid="btn-recompute-cash-all"
                  >
                    {(cashCacheClearing || cashBackfillRunning) ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Running…</>
                    ) : (
                      <><RefreshCw className="h-3.5 w-3.5 mr-2" /> Recompute Current Year</>
                    )}
                  </Button>
                  <label className="flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer select-none" data-testid="force-recompute-all-label">
                    <input
                      type="checkbox"
                      checked={forceRecompute}
                      onChange={(e) => setForceRecompute(e.target.checked)}
                      disabled={cashCacheClearing || cashBackfillRunning}
                      className="h-3.5 w-3.5 accent-emerald-600"
                      data-testid="force-recompute-all-checkbox"
                    />
                    Force full re-fetch (fix short days)
                  </label>
                  <Button
                    onClick={backfillPriorYearCash}
                    disabled={cashCacheClearing || cashBackfillRunning}
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
                    data-testid="btn-backfill-cash-2025"
                  >
                    <Calendar className="h-3.5 w-3.5 mr-2" /> Backfill 2025 (YoY)
                  </Button>
                </div>
              </div>

              {/* ── Full Rebuild (all locations, payment-date accurate) ── */}
              <div className="mt-3 pt-3 border-t border-emerald-100">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 text-xs text-gray-600 max-w-2xl">
                    <span className="font-semibold text-gray-900 text-sm">Full Rebuild — All Locations (2024 → today)</span>
                    <p className="mt-1">
                      Scans <strong>every invoice since 2024</strong> for each location and buckets each payment by
                      the day it was collected. This captures advance deposits, prepaid packages and recurring
                      memberships that the normal recompute misses, making <strong>Cash Collected match Gingr&rsquo;s
                      Net Payments</strong> system-wide (and rebuilds 2024&ndash;2025 for accurate YoY). Heavy one-time
                      job (~20 min/location), runs in the background &mdash; <strong>best run off-hours</strong>.
                    </p>
                  </div>
                  <Button
                    onClick={startRebuildAll}
                    disabled={rebuildAllRunning}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap shrink-0"
                    data-testid="btn-rebuild-all"
                  >
                    {rebuildAllRunning ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Rebuilding…</>
                    ) : (
                      <><RefreshCw className="h-3.5 w-3.5 mr-2" /> Rebuild All Locations</>
                    )}
                  </Button>
                </div>
                {rebuildAllStatus && rebuildAllStatus.status && rebuildAllStatus.status !== "not_started" && (
                  <div className="mt-2 text-[11px] text-gray-600" data-testid="rebuild-all-progress">
                    <div className="flex items-center flex-wrap gap-x-2">
                      <span className="font-semibold">Rebuild-all: {rebuildAllStatus.status}</span>
                      {typeof rebuildAllStatus.completed_locations === "number" && rebuildAllStatus.total_locations
                        ? <span>— {rebuildAllStatus.completed_locations}/{rebuildAllStatus.total_locations} locations ({rebuildAllStatus.progress_pct ?? 0}%)</span>
                        : null}
                      {(rebuildAllStatus.status === "running" || String(rebuildAllStatus.status || "").startsWith("retrying")) && (
                        <button
                          onClick={stopRebuildAll}
                          data-testid="btn-stop-rebuild-all"
                          className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
                        >
                          <X className="h-3 w-3" /> Stop
                        </button>
                      )}
                    </div>

                    {fmtRebuildWindow(rebuildAllStatus.started_at, rebuildAllStatus.completed_at) && (
                      <div className="mt-0.5 text-[11px] text-gray-500" data-testid="rebuild-all-window">
                        {fmtRebuildWindow(rebuildAllStatus.started_at, rebuildAllStatus.completed_at)}
                        {rebuildAllStatus.parallel_lanes > 1
                          ? ` · ${rebuildAllStatus.parallel_lanes} parallel lanes (grouped by Gingr key)` : ""}
                        {rebuildAllStatus.running_count > 1
                          ? ` · ${rebuildAllStatus.running_count} running now` : ""}
                      </div>
                    )}

                    {rebuildAllStatus.current_progress && (
                      <div className="mt-1" data-testid="rebuild-all-current">
                        <span className="font-medium">{rebuildAllStatus.current_progress.location_name || rebuildAllStatus.current_location_name}</span>
                        {` · window ${rebuildAllStatus.current_progress.windows_done}/${rebuildAllStatus.current_progress.windows_total} (${rebuildAllStatus.current_progress.progress_pct}%)`}
                        {rebuildAllStatus.current_progress.current_window ? ` · through ${rebuildAllStatus.current_progress.current_window}` : ""}
                        {typeof rebuildAllStatus.current_progress.seconds_since_update === "number"
                          ? ` · updated ${fmtAgo(rebuildAllStatus.current_progress.seconds_since_update)} ago` : ""}
                        <div className="mt-1 h-1.5 w-full max-w-xs bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 transition-all"
                            style={{ width: `${rebuildAllStatus.current_progress.progress_pct || 0}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {rebuildAllStatus.stalled && (
                      <div className="mt-1 flex items-start gap-1.5 text-amber-700 font-medium" data-testid="rebuild-all-stalled">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                          No progress for {fmtAgo(rebuildAllStatus.stalled_seconds)} — the background job appears stalled.
                          It auto-resumes on the next backend restart; or click "Rebuild All Locations" to restart it now.
                        </span>
                      </div>
                    )}

                    {rebuildAllStatus.status === "interrupted" && (
                      <div className="mt-1 flex items-start gap-1.5 text-amber-700 font-medium" data-testid="rebuild-all-interrupted">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>Interrupted by a restart and hit the auto-resume cap. Click "Rebuild All Locations" to restart.</span>
                      </div>
                    )}

                    {Array.isArray(rebuildAllStatus.locations) && (
                      <ul className="mt-1 space-y-0.5">
                        {rebuildAllStatus.locations.map((l) => (
                          <li key={l.location_id} data-testid={`rebuild-all-loc-${l.location_id}`}>
                            {l.name}: <span className="font-medium">{rebuildStatusLabel(l.status)}</span>
                            {l.windows_total ? ` (${l.windows_done}/${l.windows_total} win)` : ""}
                            {l.days_written ? ` · ${l.days_written} days` : ""}
                            {l.deposits_failed ? <span className="text-amber-600"> · deposits pending (fills overnight)</span> : ""}
                            {fmtRebuildWindow(l.started_at, l.completed_at) && (
                              <span className="block text-[10px] text-gray-500 ml-3" data-testid={`rebuild-all-loc-time-${l.location_id}`}>
                                {fmtRebuildWindow(l.started_at, l.completed_at)}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Automatic reconciliation schedule (weekly fail-safe + run tonight) */}
                {rebuildSchedule && (
                  <div className="mt-3 pt-3 border-t border-indigo-100" data-testid="rebuild-schedule">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Clock className="h-3.5 w-3.5 text-indigo-600" />
                      <span className="text-xs font-semibold text-gray-900">Automatic reconciliation (off-hours)</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-2 max-w-2xl">
                      Runs the full payment-date rebuild at ~{rebuildSchedule.run_hour_et} AM ET, when Gingr is quiet &mdash; so it doesn&rsquo;t
                      fight daytime throttling. Keeps Cash Collected matching Net Payments without manual clicks.
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      {rebuildSchedule.run_once_date ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded" data-testid="rebuild-armed-badge">
                            <CheckCircle2 className="h-3 w-3" /> Armed for {rebuildSchedule.run_once_date} ~{rebuildSchedule.run_hour_et} AM ET
                          </span>
                          <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={scheduleSaving} onClick={cancelRunTonight} data-testid="btn-cancel-tonight">Cancel</Button>
                        </div>
                      ) : (
                        <Button size="sm" className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" disabled={scheduleSaving} onClick={armRunTonight} data-testid="btn-run-tonight">
                          {scheduleSaving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Clock className="h-3.5 w-3.5 mr-1.5" />}
                          Run tonight (off-hours)
                        </Button>
                      )}
                      <label className="inline-flex items-center gap-2 text-[11px] text-gray-700 cursor-pointer select-none" data-testid="toggle-weekly-rebuild">
                        <input type="checkbox" className="h-3.5 w-3.5 accent-indigo-600" checked={!!rebuildSchedule.weekly_enabled} disabled={scheduleSaving} onChange={(e) => toggleWeekly(e.target.checked)} />
                        Weekly auto-rebuild ({rebuildSchedule.weekly_weekday_name}s ~{rebuildSchedule.run_hour_et} AM ET)
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Live / last-run progress panel */}
              {cashCacheStatus && cashCacheStatus.status && cashCacheStatus.status !== "not_started" && (
                <div className="mt-3 pt-3 border-t border-gray-100" data-testid="cash-recompute-progress">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      {cashCacheStatus.status === "running" ? (
                        <span className="inline-flex items-center gap-1 text-blue-700">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Recomputing {cashCacheStatus.from_date} → {cashCacheStatus.to_date}
                          {cashCacheStatus.current_location_name ? ` · ${cashCacheStatus.current_location_name}` : ""}
                        </span>
                      ) : cashCacheStatus.status === "complete" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> Last recompute complete
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700">Last recompute failed</span>
                      )}
                    </div>
                    <span className="text-xs font-bold text-gray-700">
                      {cashCacheStatus.completed_locations}/{cashCacheStatus.total_locations} · {cashCacheStatus.progress_pct ?? 0}%
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full transition-all ${cashCacheStatus.status === "failed" ? "bg-red-400" : "bg-emerald-500"}`}
                      style={{ width: `${cashCacheStatus.progress_pct ?? 0}%` }}
                    />
                  </div>
                  {/* Per-location checklist */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {(cashCacheStatus.locations || []).map((loc) => (
                      <div key={loc.location_id} className="flex items-center justify-between text-[11px] px-2 py-1 rounded bg-gray-50" data-testid={`recompute-loc-${loc.location_id}`}>
                        <span className="flex items-center gap-1.5 font-medium text-gray-700">
                          {loc.status === "complete" ? (
                            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                          ) : loc.status === "running" ? (
                            <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                          ) : loc.status === "failed" ? (
                            <span className="h-3 w-3 text-red-500">✕</span>
                          ) : (
                            <Clock className="h-3 w-3 text-gray-300" />
                          )}
                          {loc.name}
                        </span>
                        <span className="text-gray-500">
                          {loc.status === "running" || loc.status === "complete"
                            ? `${loc.days_processed || 0}${loc.days_total ? `/${loc.days_total}` : ""} days`
                            : loc.status === "failed" ? "failed" : "pending"}
                        </span>
                      </div>
                    ))}
                  </div>
                  {cashCacheStatus.completed_at && cashCacheStatus.status !== "running" && (
                    <div className="text-[10px] text-gray-400 mt-2">
                      Finished {new Date(cashCacheStatus.completed_at).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Rebuild ONE location (clean, 2024 → today) ─────────── */}
            <div className="p-3 bg-white rounded-lg border border-indigo-200 mt-3" data-testid="single-location-recompute-card">
              <div className="font-semibold text-gray-900 text-sm">Rebuild one location (2024 → today)</div>
              <p className="text-xs text-gray-600 mt-1 max-w-2xl">
                Same clean engine as "Rebuild All", for a single site &mdash; archives the old rows, then scans
                every invoice since 2024 and buckets each payment by the day it was collected, so Cash Collected
                matches Gingr&rsquo;s Net Payments. Heavy the first time (fills the cache); re-runs are fast.
                <strong> Best run off-hours.</strong> Use this to re-do just one location (e.g. if a site shows a gap).
              </p>
              <div className="flex flex-col sm:flex-row sm:items-end gap-2 mt-3">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs text-gray-500">Location</Label>
                  <Select value={singleLocId} onValueChange={setSingleLocId}>
                    <SelectTrigger data-testid="single-recompute-location-select" className="h-9">
                      <SelectValue placeholder="Select a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={startFullRebuild}
                  disabled={rebuildRunning}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap h-9 shrink-0"
                  data-testid="btn-full-rebuild"
                >
                  {rebuildRunning ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Rebuilding…</>
                  ) : (
                    <><RefreshCw className="h-3.5 w-3.5 mr-2" /> Rebuild This Location</>
                  )}
                </Button>
                {rebuildRunning && (
                  <Button
                    onClick={stopFullRebuild}
                    variant="destructive"
                    className="whitespace-nowrap h-9 shrink-0"
                    data-testid="btn-stop-full-rebuild"
                  >
                    <X className="h-3.5 w-3.5 mr-2" /> Stop
                  </Button>
                )}
              </div>
              {rebuildStatus && rebuildStatus.status !== "not_started" && (
                <div className="mt-2 text-[11px] text-gray-600" data-testid="full-rebuild-progress">
                  Rebuild: <span className="font-semibold">{rebuildStatus.status}</span>
                  {rebuildStatus.windows_total ? ` — ${rebuildStatus.windows_done || 0}/${rebuildStatus.windows_total} windows (${rebuildStatus.progress_pct || 0}%)` : ""}
                  {rebuildStatus.current_window ? ` · through ${rebuildStatus.current_window}` : ""}
                  {rebuildStatus.days_written ? ` · ${rebuildStatus.days_written} days written` : ""}
                  {rebuildStatus.failed_windows ? ` · ${rebuildStatus.failed_windows} failed window(s)` : ""}
                  {rebuildStatus.error ? ` · ${rebuildStatus.error}` : ""}
                  {rebuildStatus.stalled && (
                    <span className="text-red-600 font-semibold"> · ⚠ no progress recently — use Stop to clear</span>
                  )}
                  {fmtRebuildWindow(rebuildStatus.started_at, rebuildStatus.completed_at) && (
                    <span className="block text-[10px] text-gray-500 mt-0.5" data-testid="full-rebuild-window">
                      {fmtRebuildWindow(rebuildStatus.started_at, rebuildStatus.completed_at)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AdminUsers;
